// src/components/catalog/BundleEditModal.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import { updateBundle, readApiError, familyLabel, scopeLabel } from '../../services/catalogApi';
import styles from './BundleEditModal.module.css';

/**
 * Bundle içeriğini düzenler: PATCH /v2/admin/bundles/{id}.
 * Gönderilmeyen alana dokunulmaz, bu yüzden yalnızca DEĞİŞEN alanlar gönderilir.
 *
 * Not: sınırsız bundle'ların çevirisi henüz düzenlenemiyor (bundle_translations
 * tablosu var ama admin ucu yok). Bugün title/description tek dilde — sağlayıcının
 * verdiği İngilizce metin — ve buradan elle düzeltilebilir.
 */
const BundleEditModal = ({ isOpen, onClose, bundle, onSaved }) => {
    const [form, setForm] = useState({ title: '', description: '', imgUrl: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen || !bundle) return;
        setForm({
            title: bundle.title || '',
            description: bundle.description || '',
            imgUrl: bundle.imgUrl || '',
        });
        setError(null);
    }, [isOpen, bundle]);

    if (!bundle) return null;

    const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    // Yalnızca değişen alanlar. Boşaltılan alan boş string olarak gider
    // (null göndermek "dokunma" ile karışırdı).
    const buildPatch = () => {
        const patch = {};
        if (form.title.trim() !== (bundle.title || '')) patch.title = form.title.trim();
        if (form.description.trim() !== (bundle.description || '')) patch.description = form.description.trim();
        if (form.imgUrl.trim() !== (bundle.imgUrl || '')) patch.imgUrl = form.imgUrl.trim();
        return patch;
    };

    const patch = buildPatch();
    const isDirty = Object.keys(patch).length > 0;

    const handleSave = async () => {
        if (!isDirty) {
            onClose();
            return;
        }
        if (!form.title.trim()) {
            setError('Başlık boş olamaz.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await updateBundle(bundle.id, patch);
            toast.success('Paket bilgileri güncellendi.');
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error(`Bundle ${bundle.id} güncellenemedi:`, err);
            setError(readApiError(err, 'Güncelleme başarısız oldu.').message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Paketi düzenle: ${bundle.title || bundle.id}`}>
            <div className={styles.body}>
                <div className={styles.metaRow}>
                    <span className={styles.chip}>{familyLabel(bundle.family)}</span>
                    <span className={styles.chip}>{scopeLabel(bundle.scope)}</span>
                    <span className={`${styles.metaId} num`}>id:{bundle.id}</span>
                    {bundle.providerRef && (
                        <span className={`${styles.metaId} num`}>sağlayıcı:#{bundle.providerRef}</span>
                    )}
                </div>

                <div className={styles.field}>
                    <label htmlFor="be-title">Başlık</label>
                    <input
                        id="be-title"
                        type="text"
                        value={form.title}
                        onChange={setField('title')}
                        disabled={saving}
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="be-description">Açıklama</label>
                    <textarea
                        id="be-description"
                        rows={4}
                        value={form.description}
                        onChange={setField('description')}
                        disabled={saving}
                    />
                </div>

                <div className={styles.field}>
                    <label htmlFor="be-img">Görsel URL</label>
                    <input
                        id="be-img"
                        type="text"
                        value={form.imgUrl}
                        onChange={setField('imgUrl')}
                        disabled={saving}
                    />
                    {form.imgUrl.trim() && (
                        <div className={styles.preview}>
                            <img src={form.imgUrl} alt="Görsel önizleme" />
                            <span className={styles.hint}>Önizleme</span>
                        </div>
                    )}
                </div>

                <div className={styles.infoBox}>
                    Çeviri düzenlemesi henüz yok — metinler tek dilde tutuluyor. Sağlayıcıdan gelen
                    İngilizce başlık/açıklamayı buradan elle Türkçeleştirebilirsiniz.
                </div>

                {error && <p className={styles.errorText}>{error}</p>}

                <div className={styles.actions}>
                    <button type="button" className="ghost" onClick={onClose} disabled={saving}>
                        Vazgeç
                    </button>
                    <button type="button" onClick={handleSave} disabled={saving || !isDirty}>
                        {saving ? 'Kaydediliyor…' : 'Kaydet'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default BundleEditModal;
